// @givmo/app-admin — sign-in EMAIL/PASSWORD screen (/sign-in). Ports `sign_in_screen.dart`.
//
// Email + password + "Remember me" + "Forgot password?" + "Log in" + footer "Don't have an account?
// Get started". When `?redirect-block=true` (the Flutter `isRedirect`), the magenta redirect block
// shows above the heading ("You already created an account with your work email on the Givmo mobile
// app…"). On submit @givmo/auth applies rememberMe persistence (we DON'T call setPersistence) and
// resolves the routing state, which we map onto routes. Normal disabled-until-valid Log in button
// (the Flutter always-tappable workaround is dropped; the VALIDATION LOGIC is preserved in the Zod
// schema). Email format failure is the inline `Invalid email format` (no network call).
//
// Drawn in the register of the portal's sign-in chooser (`sign-in-main-screen.tsx`), its next screen
// (EP-WEB43): its heading, the SaaS button, brand-coloured text links, its footer line under a
// hairline; the redirect block is the console door's soft notice.

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useSearch } from '@tanstack/react-router';
import {
  SignInDoorError,
  organizationSsoLabel,
  useOrganizationSsoAvailability,
} from '@givmo/passkeys';
import {
  Button,
  Checkbox,
  Form,
  FormField,
  TextField,
  useGivmoForm,
  testIdProp,
} from '@givmo/design-system';
import { secondFactorChallengeOf, type AdminSignInResult, type TotpChallenge } from '@givmo/auth';
import { useAuth } from '../../auth/auth-context';
import { AuthShell } from './auth-shell';
import { ForgotPasswordDialog } from './forgot-password-dialog';
import { TotpChallengePrompt } from './totp-challenge';
import { useAuthError } from './use-auth-error';
import { AUTH_ROUTES, routeForRoutingState } from './auth-routing';
import { landSignInWithoutCompany } from './companyless-landing';
import { signInSchema, INVALID_EMAIL_MESSAGE, type SignInFormValues } from './auth-schemas';

const REDIRECT_BLOCK_COPY =
  'You already created an account with your work email on the Givmo mobile app. Please sign in with those credentials here:';

export function SignInScreen() {
  const { signInWithEmail } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { redirect, 'redirect-block': redirectBlock } = useSearch({ from: '/sign-in' });
  // EP-SUITE2-FIX6: the sign-in signs the account in before its tail can fail (a company it cannot
  // find), so the error must outlive the identity boundary's remount — the door's key.
  const { error, lockRefused, clearError, surface } = useAuthError('sign-in');
  // The way back in for someone their company locks out (AS-WEB26/27), returning to this form.
  const here = useLocation({ select: (location) => location.href });
  const openEscapeHatch = () =>
    void navigate({ to: AUTH_ROUTES.lostCompanyAccess, search: { back: here } });
  // AS-WEB30: the lock notice offers the organization door first, where it can work here; its step
  // lands where this form would, and returns here.
  const ssoAvailable = useOrganizationSsoAvailability();
  const organizationSso = ssoAvailable
    ? {
        label: organizationSsoLabel('signIn'),
        onOpen: () =>
          void navigate({
            to: AUTH_ROUTES.organizationSso,
            search: { ...(redirect ? { redirect } : {}), back: here },
          }),
      }
    : undefined;
  const [submitting, setSubmitting] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [challenge, setChallenge] = useState<TotpChallenge<AdminSignInResult> | null>(null);

  const form = useGivmoForm(signInSchema, {
    defaultValues: { email: '', password: '', rememberMe: true },
    mode: 'onTouched',
  });

  const onSubmit = async (values: SignInFormValues) => {
    clearError();
    setSubmitting(true);
    try {
      const result = await signInWithEmail(values);
      await navigate({ to: routeForRoutingState(result.state, redirect) });
    } catch (thrown) {
      // An enrolled admin's password was accepted and a second factor is now owed (AS-WEB1). That is
      // a state of this sign-in, not a failure of it — swap the form for the code prompt.
      const pending = secondFactorChallengeOf<AdminSignInResult>(thrown);
      if (pending !== null) {
        setChallenge(pending);
        return;
      }
      // EP-SUITE2-FIX5: a sign-in that found no company left a SIGNED-IN account behind — land it
      // (into the portal, or on its funnel screen) rather than fail a sign-in that succeeded. An
      // account no landing fits, and every other failure, sees the error as before.
      const landing = await landSignInWithoutCompany(thrown, queryClient);
      if (landing !== null) await navigate(landing);
      else surface(thrown);
    } finally {
      setSubmitting(false);
    }
  };

  if (challenge !== null) {
    return (
      <AuthShell testId="page-sign-in">
        <TotpChallengePrompt
          door="sign-in"
          challenge={challenge}
          onResolved={(result) => navigate({ to: routeForRoutingState(result.state, redirect) })}
          onCancel={() => {
            clearError(); // the prompt's error is the door's slot; the form starts clean
            setChallenge(null);
          }}
          onUnlink={openEscapeHatch}
          organizationSso={organizationSso}
        />
      </AuthShell>
    );
  }

  return (
    <AuthShell testId="page-sign-in">
      <div className="flex flex-col gap-4">
        {redirectBlock === true && (
          <p
            className="mb-12 rounded-lg bg-brand-50 px-14 py-10 font-sans text-s13 font-w500 leading-h15 tracking-givmo text-brand-800"
            {...testIdProp('auth-redirect-block')}
          >
            {REDIRECT_BLOCK_COPY}
          </p>
        )}
        <h1 className="font-sans text-s22 font-w700 tracking-givmo text-text-strong">
          Sign in to admin portal
        </h1>
      </div>

      <Form form={form} onSubmit={onSubmit} testId="auth-signin-form">
        <FormField
          name="email"
          render={({ field, invalid }) => (
            <TextField
              label="Email address"
              type="email"
              autoComplete="email"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={invalid}
              errorMessage={INVALID_EMAIL_MESSAGE}
              testId="auth-email"
            />
          )}
          hideError
        />
        <FormField
          name="password"
          render={({ field, invalid, error: fieldError }) => (
            <TextField
              label="Password"
              type="password"
              autoComplete="current-password"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={invalid}
              errorMessage={fieldError}
              testId="auth-password"
            />
          )}
          hideError
        />

        <div className="flex items-center justify-between">
          <FormField
            name="rememberMe"
            render={({ field }) => (
              <label className="flex items-center gap-8 font-sans text-s13 font-w400 tracking-givmo text-text-secondary">
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  testId="auth-remember"
                />
                Remember me
              </label>
            )}
            hideError
          />
          <button
            type="button"
            onClick={() => setForgotOpen(true)}
            className="font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline"
            {...testIdProp('auth-forgot')}
          >
            Forgot password?
          </button>
        </div>

        {/* AS-WEB27: the company lock's refusal is the notice with the way back in; anything else
            the red line. */}
        <SignInDoorError
          error={error}
          lockRefused={lockRefused}
          onUnlink={openEscapeHatch}
          organizationSso={organizationSso}
        />

        <Button
          type="submit"
          appearance="saas"
          tone="primary"
          size="lg"
          fullWidth
          loading={submitting}
          disabled={!form.formState.isValid || submitting}
          testId="auth-login"
        >
          Log in
        </Button>
      </Form>

      <p className="border-t border-border-subtle pt-20 text-center font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
        Don’t have an account?{' '}
        <button
          type="button"
          onClick={() => void navigate({ to: AUTH_ROUTES.signUp })}
          className="font-w600 text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline"
          {...testIdProp('auth-get-started')}
        >
          Get started
        </button>
      </p>

      <ForgotPasswordDialog
        open={forgotOpen}
        onOpenChange={setForgotOpen}
        initialEmail={form.getValues('email')}
      />
    </AuthShell>
  );
}
